import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Layout } from './components/Layout';
import { LoadingScreen } from './components/LoadingScreen';
import { ProtectedRoute } from './guards/ProtectedRoute';
import { useAuth } from './auth/AuthProvider';
import { LoginPage } from './pages/Login';

// Páginas são carregadas sob demanda: com todas importadas de forma estática o
// build saía num chunk único de ~870 KB (263 KB br) que toda visita baixava e
// parseava inteiro — inclusive telas de admin que a maioria dos usuários nunca
// abre. Login fica estático de propósito: é a primeira pintura de quem não tem
// sessão, e adiar o chunk dele só adicionaria um round-trip antes do formulário.
const AdminCreatorsPage = lazy(() => import('./pages/AdminCreators').then((m) => ({ default: m.AdminCreatorsPage })));
const AdminEntitiesPage = lazy(() => import('./pages/AdminEntities').then((m) => ({ default: m.AdminEntitiesPage })));
const AdminHouseParamsPage = lazy(() => import('./pages/AdminHouseParams').then((m) => ({ default: m.AdminHouseParamsPage })));
const AdminUserPermissionsPage = lazy(() => import('./pages/AdminUserPermissions').then((m) => ({ default: m.AdminUserPermissionsPage })));
const AdminUsersPage = lazy(() => import('./pages/AdminUsers').then((m) => ({ default: m.AdminUsersPage })));
const ApiKeys = lazy(() => import('./pages/ApiKeys').then((m) => ({ default: m.ApiKeys })));
const CreatorGeneratorPage = lazy(() => import('./pages/CreatorGenerator').then((m) => ({ default: m.CreatorGeneratorPage })));
const HistoryPage = lazy(() => import('./pages/History').then((m) => ({ default: m.HistoryPage })));
const ProfilePage = lazy(() => import('./pages/Profile').then((m) => ({ default: m.ProfilePage })));
const RedirectManagerPage = lazy(() => import('./pages/RedirectManager').then((m) => ({ default: m.RedirectManagerPage })));
const TrafficGeneratorPage = lazy(() => import('./pages/TrafficGenerator').then((m) => ({ default: m.TrafficGeneratorPage })));
const VerifyLinkPage = lazy(() => import('./pages/VerifyLink').then((m) => ({ default: m.VerifyLinkPage })));

// Sem esse wrapper as transações do Sentry usam a URL crua
// (/admin/users/42/permissions) em vez do padrão da rota (/admin/users/:id/permissions).
const SentryRoutes = Sentry.wrapReactRouterRouting(Routes);

function IndexRoute() {
  const { user, permissions } = useAuth();
  if (user?.role === 'viewer') return <Navigate to="/history" replace />;
  const canAccessMain = user?.role === 'admin' || (permissions?.generatorAccess?.canAccessMain ?? true);
  if (!canAccessMain) return <Navigate to="/history" replace />;
  return <CreatorGeneratorPage />;
}

export default function App() {
  return (
    // Boundary externo cobre as rotas fora do Layout. As de dentro têm o próprio
    // Suspense em volta do <Outlet /> (Layout.tsx) pra que sidebar e topo não
    // pisquem enquanto o chunk da rota nova chega.
    <Suspense fallback={<LoadingScreen message="Carregando" />}>
      <SentryRoutes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<IndexRoute />} />
          <Route path="/me" element={<ProfilePage />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/traffic-generator" element={<TrafficGeneratorPage />} />
          <Route path="/redirect-manager" element={<RedirectManagerPage />} />
          <Route path="/verificar-link" element={<VerifyLinkPage />} />
          <Route
            path="/admin/api-keys"
            element={
              <ProtectedRoute requireAdmin>
                <ApiKeys />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/entities"
            element={
              <ProtectedRoute requireAdmin>
                <AdminEntitiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/entities/bet-houses/:id/params"
            element={
              <ProtectedRoute requireAdmin>
                <AdminHouseParamsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/creators"
            element={
              <ProtectedRoute requireAdmin>
                <AdminCreatorsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requireAdminOrManager>
                <AdminUsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users/:id/permissions"
            element={
              <ProtectedRoute requireAdminOrManager>
                <AdminUserPermissionsPage />
              </ProtectedRoute>
            }
          />
        </Route>
      </SentryRoutes>
    </Suspense>
  );
}
